import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { formatDate } from "../../api/dates";
import { KIND_LABEL, useChangeKind, useInspection, type InspectionDetail, type InspectionKind } from "../../api/inspections";
import { StatusChip } from "../../components/ui";
import ChecksTab from "./ChecksTab";
import GeneralTab from "./GeneralTab";
import IdentityTab from "./IdentityTab";
import { STATUS_TONE } from "./InspectionList";
import MeasurementTab from "./MeasurementTab";
import SaveState from "./SaveState";
import SectionRail, { sectionStates, type Tab } from "./SectionRail";
import SparesTab from "./SparesTab";
import SubmitTab from "./SubmitTab";
import ValveStrip from "./ValveStrip";

export type { Tab } from "./SectionRail";

interface Props {
  id: number;
}

/** One inspection: who, where and when; the valve strip; then the checklist's
 * sections as a rail of steps. Section 1 and the submission are the
 * inspection's; sections 2, 3, 4, 9 and the checks are the valve's, and the
 * valve travels in the URL (`?valve=`) so a tablet left open cannot save one
 * valve under another. */
export default function InspectionWorkspace({ id }: Props) {
  const [params, setParams] = useSearchParams();
  const raw = params.get("valve");
  const valve = raw === null ? null : Number(raw);
  const detail = useInspection(id, valve);
  if (detail.isPending) {
    return <p className="app-loading">Loading inspection…</p>;
  }
  if (detail.isError) {
    return <p className="notice bad">The inspection could not be opened: {detail.error.message}</p>;
  }
  return (
    <InspectionView
      detail={detail.data}
      onSelectValve={(valveId) => {
        const next = new URLSearchParams(params);
        next.set("valve", String(valveId));
        setParams(next);
      }}
    />
  );
}

export function InspectionView({ detail, onSelectValve }: { detail: InspectionDetail; onSelectValve: (valveId: number) => void }) {
  const [tab, setTab] = useState<Tab>("general");
  const [onlyOpen, setOnlyOpen] = useState(false);
  const head = detail.inspection;
  const states = sectionStates(detail);

  /** From the submit checklist: straight to that valve's unanswered checks. */
  function answer(valveId: number) {
    setOnlyOpen(true);
    setTab("checks");
    if (valveId !== detail.active_valve_id) {
      onSelectValve(valveId);
    }
  }

  return (
    <section className="insp" aria-label={`Inspection ${head.work_order_no}`}>
      <InspectionHead detail={detail} />
      {detail.notice ? <p className="notice">{detail.notice}</p> : null}
      <ValveStrip detail={detail} onSelectValve={onSelectValve} />
      <SectionRail states={states} active={tab} onSelect={setTab} />
      <div className="card insp-panel" role="tabpanel" id={`panel-${tab}`} aria-labelledby={`tab-${tab}`}>
        {/* Keyed on the form: a switch stamps the work type, and the tab's
            seeded state must take it up before its next save sends it back. */}
        {tab === "general" ? <GeneralTab key={`${head.id}:${head.kind}`} detail={detail} /> : null}
        {tab === "drilling" ? <MeasurementTab detail={detail} /> : null}
        {tab === "identity" ? <IdentityTab detail={detail} /> : null}
        {tab === "spares" ? <SparesTab detail={detail} /> : null}
        {tab === "checks" ? (
          <ChecksTab
            inspectionId={head.id}
            valveId={detail.active_valve_id}
            valveName={detail.valves.find((v) => v.id === detail.active_valve_id)?.name ?? null}
            canEdit={detail.actions.can_edit}
            onlyOpen={onlyOpen}
            onOnlyOpen={setOnlyOpen}
          />
        ) : null}
        {tab === "submit" ? <SubmitTab detail={detail} onAnswer={answer} /> : null}
      </div>
    </section>
  );
}

/** The works order large, its state and kind of work; the site; the field
 * operators, the date and where it came from; and the last save. On a phone
 * the list is hidden behind this, so the way back is here. */
function InspectionHead({ detail }: { detail: InspectionDetail }) {
  const head = detail.inspection;
  const team = detail.options.teams.find((t) => t.id === head.team_id)?.name ?? head.team_name;
  const meta = [team, head.inspection_date ? formatDate(head.inspection_date) : null, head.work_order_from_route ? "from a route plan" : null].filter(
    (part): part is string => !!part,
  );
  return (
    <header className="insp-head">
      <div className="insp-head-main">
        <Link to="/inspections" className="insp-back">
          ‹ Inspections
        </Link>
        <div className="insp-title">
          <h2 className="order-number">{head.work_order_no}</h2>
          <span className={`order-state status-chip tone-${STATUS_TONE[head.status] ?? "quiet"}`}>{head.is_draft ? "Draft" : "Submitted"}</span>
          <StatusChip>{head.kind_label}</StatusChip>
          {head.work_type_label ? <StatusChip>{head.work_type_label}</StatusChip> : null}
        </div>
        <span className="insp-site">{detail.site?.name ?? head.chamber_name ?? "No site or chamber yet"}</span>
        {meta.length > 0 ? <span className="insp-meta">{meta.join(" · ")}</span> : null}
      </div>
      <div className="insp-head-actions">
        <SaveState inspectionId={head.id} />
        {detail.actions.can_change_kind ? <KindSwitch detail={detail} /> : null}
        {detail.boq ? (
          <Link className="btn" to={detail.boq.href}>
            BOQ
          </Link>
        ) : null}
        {detail.site ? (
          <Link className="btn" to={`/sites/${detail.site.id}`}>
            Site
          </Link>
        ) : null}
      </div>
    </header>
  );
}

/** The office's change of form, offered only while the draft holds nothing on
 * its form (`actions.can_change_kind`); the server refuses it otherwise. */
function KindSwitch({ detail }: { detail: InspectionDetail }) {
  const change = useChangeKind(detail.inspection.id);
  const other: InspectionKind = detail.inspection.kind === "pm42" ? "pm41" : "pm42";
  return (
    <>
      <button type="button" className="btn" disabled={change.isPending} onClick={() => change.mutate(other)}>
        Switch to {KIND_LABEL[other]}
      </button>
      {change.isError ? <span className="notice bad">{change.error.message}</span> : null}
    </>
  );
}
